/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { ComponentProps } from 'svelte'
import type {
	DashboardReportParametersInput,
	DashboardReportMetadataQuery$result,
	DashboardReportShareType$options,
	DashboardReportShareType,
	ValueOf,
} from '$houdini'
import type {
	Chart,
	ChartBase,
	ChartJson,
	DashboardReportDateRangeParameter,
	DashboardReportParameter,
	DashboardReportSelectionParameter,
	DashboardReportSiteSelectionParameter,
	Formatting,
	GoogleDataTableColumn,
	GoogleDataTableRow,
	OptionList,
	Table,
	TableColumn,
	TableRow,
} from '@isoftdata/utility-dashboard-backend'
import type {
	ChartDataForReportReturn,
	Dashboard,
	Parameter,
	ReportInput,
	ReportMetadataReturn,
	ReportTitlesForUserReturn,
} from '@isoftdata/svelte-dashboard'
import type { NamedDateRange } from '@isoftdata/utility-date-time'

import { graphql } from '$houdini'

export async function loadDashboardReportTitlesForUser(useCache = true): ReportTitlesForUserReturn {
	const titles =
		(
			await reportTitlesQuery.fetch({
				variables: {
					args: {
						sharedWithCurrentUser: true,
					},
				},
				// don't cache when called in resolve function, but use cache when called in the component?
				policy: useCache ? 'CacheOrNetwork' : undefined,
			})
		)?.data?.dashboardReports ?? []

	return titles.map((report): Awaited<ReportTitlesForUserReturn>[number] => {
		return {
			...report,
			dashboardReportId: report.dashboardReportId,
			title: report.title,
			parameterValues: [],
			// Unknown until the report's metadata loads. Dashboard keeps any non-null interval already on the
			// report as the user's choice, so a placeholder number here would override the configured one.
			autoRefreshInterval: null,
			hideParameters: false,
			charts: [],
			description: '',
		}
	})
}

function parseJSON<T>(json: string): T | null {
	try {
		return JSON.parse(json) as T
	} catch (e) {
		return null
	}
}

export async function loadChartDataForReport({
	dashboardReportId,
	chartId,
	parameterSelectionList: inputParameterSelectionList,
}: Parameters<ComponentProps<typeof Dashboard>['loadChartDataForReport']>[0]): ChartDataForReportReturn {
	const parameterSelectionList: Array<DashboardReportParametersInput> = inputParameterSelectionList.map(
		(param): DashboardReportParametersInput => {
			return {
				name: param.name,
				value: param.value ?? '',
				dates: 'dates' in param ? param.dates : undefined,
			}
		},
	)

	const dashboardReport = (
		await chartDataQuery.fetch({
			variables: {
				dashboardReportId,
				chartArgs: {
					id: chartId,
					inReport: true,
				},
				parameterSelectionList,
			},
		})
	)?.data?.dashboardReport

	if (!dashboardReport?.charts) {
		throw new Error(`No charts found for report ${dashboardReportId}.`)
	}
	const matchingChart = dashboardReport.charts.find(chart => chart && chart.chartId === chartId)
	if (!matchingChart) {
		throw new Error(`Chart with ID ${chartId} not found in report ${dashboardReportId}.`)
	}
	const { json, dataTable: constDataTable, formatting, ...constChart } = matchingChart
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { query, formatting: unprocessedFormatting, ...jsonProps } = JSON.parse(json) as ChartJson // don't include query, unprocessed formatting

	const googleDataTableFromApi =
		constDataTable && constDataTable.__typename === 'GoogleDataTable' ? constDataTable : undefined
	const tableDataTableFromApi =
		constDataTable && constDataTable.__typename === 'RactiveDataTable' ? constDataTable : undefined

	const parsedFormatting = formatting ? (JSON.parse(formatting) as Formatting) : undefined
	const supertype: 'google' | 'table' | 'embed' =
		(constChart.supertype.toLowerCase() as 'google' | 'table' | 'embed') ?? 'google'

	const chart: Omit<Chart, 'query'> = {
		formatting: parsedFormatting,
		...constChart,
		...jsonProps,
		supertype,
		rank: constChart.rank ?? undefined,
		multiSeries: constChart.multiSeries
			? {
					...constChart.multiSeries,
					tooltip: constChart.multiSeries.tooltip ?? '',
				}
			: undefined,
		chartWrapper: {
			...jsonProps.chartWrapper,
			dataTable:
				supertype === 'google'
					? {
							cols: (googleDataTableFromApi?.cols as Array<GoogleDataTableColumn>) ?? [],
							rows: parseJSON<Array<GoogleDataTableRow>>(googleDataTableFromApi?.rows ?? '[]') ?? [],
						}
					: undefined,
		},
		table:
			supertype === 'table'
				? {
						...jsonProps.table,
						rows: JSON.parse(tableDataTableFromApi?.rows ?? '[]') as Array<TableRow>,
						columns: (tableDataTableFromApi?.columns as Array<TableColumn>) ?? [],
					}
				: undefined,
		showChart: true,
	}

	return chart
}

export async function loadDashboardReportMetadata({
	dashboardReportId,
	dashboardChartId = null,
}: {
	dashboardReportId: number
	dashboardChartId?: number | null
}): ReportMetadataReturn {
	const { data } = await dashboardReportQuery.fetch({
		variables: {
			dashboardReportId,
			chartArgs: {
				id: dashboardChartId,
				inReport: true,
			},
		},
	})

	if (!data?.dashboardReport) {
		throw new Error(`No data returned for report ${dashboardReportId}.`)
	}

	const dashboardReport = data.dashboardReport

	const returnDashboardReport = formatReportMetadataOutput(dashboardReport)
	console.log('Dashboard report metadata loaded:', returnDashboardReport)

	return returnDashboardReport
}

export function isNonNullable<T>(value: T | null | undefined): value is T {
	return value !== null && value !== undefined
}

function fixOptionList(
	optionList: Array<{
		id: string | null
		name: string | null
		fromQuery: boolean | null
		rawSql?: boolean | null
	} | null> | null,
): OptionList {
	return (
		optionList?.map(option => ({
			id: option!.id!,
			name: option!.name!,
			rawSql: option!.rawSql ?? false,
			fromQuery: option!.fromQuery ?? false,
		})) ?? []
	)
}

export function chartToUnionType(chart: {
	chartId: number
	name: string
	title: string
	rank?: number | null
	json: string
	supertype: string | null
	showChart: boolean
}): Chart {
	const parsedJSON = parseJSON<ChartJson>(chart.json) ?? null
	if (!parsedJSON) {
		throw new Error(`Failed to parse JSON for chart ${chart.chartId}.`)
	}
	const chartBase: Omit<ChartBase, 'supertype'> = {
		...parsedJSON,
		chartId: chart.chartId,
		name: chart.name,
		showChart: chart.showChart ?? true,
		title: chart.title,
		chartWrapper: parsedJSON.chartWrapper,
		description: '',
	}

	if (chart.supertype?.toLowerCase() === 'embed') {
		return {
			...chartBase,
			supertype: 'embed',
			url: chartBase?.url ?? '',
			query: undefined,
		}
	} else if (chart.supertype?.toLowerCase() === 'table') {
		return {
			...chartBase,
			supertype: 'table',
			query: chartBase.query ?? '',
			url: undefined,
			table: parsedJSON.table ?? ({} satisfies Omit<Table, 'rows' | 'columns'> as Table),
		}
	} else if (chart.supertype?.toLowerCase() === 'google') {
		return {
			...chartBase,
			chartId: chart.chartId,
			supertype: 'google',
			query: chartBase.query ?? '',
		}
	}
	throw new Error(`Unknown chart supertype: ${chart.supertype ?? 'null'}`)
}

export function formatReportMetadataOutput(
	dashboardReport: DashboardReportMetadataQuery$result['dashboardReport'],
): Awaited<ReportMetadataReturn> {
	if (!dashboardReport) {
		throw new Error('Dashboard report is null or undefined')
	}
	return {
		autoRefreshInterval: dashboardReport.autoRefreshInterval ?? 0,
		dashboardReportId: dashboardReport.dashboardReportId,
		charts: dashboardReport.charts?.filter(isNonNullable)?.map(chartToUnionType) ?? [],
		description: '',
		name: dashboardReport.name,
		ownerId: dashboardReport.ownerId,
		// @ts-expect-error it wants queryvalues but nah
		parameterValues: dashboardReport.parameterValues.map(apiParameterToDashboardReportParameter),
		title: dashboardReport.title,
		// TODO: get from backend?
		hideParameters: false,
		shareId: dashboardReport.shareId ?? null,
		shareType: dashboardReport.shareType,
	}
}

export function formatReportOutput({
	json,
	dashboardReportId,
	reportTitle,
	reportName,
	autoRefreshInterval,
	...dashboardReport
}: {
	dashboardReportId: number
	reportName: string
	reportTitle: string
	// Depending on the context, this may come from the JSON or be a real property
	autoRefreshInterval?: number
	json: string
	shareType: ValueOf<typeof DashboardReportShareType> | null
	shareId: number | null
	ownerId: number | null
}): ReportInput {
	if (!dashboardReport || !dashboardReportId) {
		throw new Error('Dashboard report is null or undefined')
	}

	const parsedJson = parseJSON<{ parameters: Array<Parameter>; autoRefreshInterval?: number }>(json) ?? {
		parameters: [],
		autoRefreshInterval,
	}

	return {
		...dashboardReport,
		...parsedJson,
		reportTitle: reportTitle ?? '',
		reportName: reportName ?? '',
		dashboardReportId,
		autoRefreshInterval: autoRefreshInterval ?? parsedJson.autoRefreshInterval ?? 0,
		shareType: apiShareTypeToClientType(dashboardReport.shareType),
	}
}

type NoQueryValues<T> = Omit<T, 'queryValues'>

function apiShareTypeToClientType(shareType: DashboardReportShareType$options | null): ReportInput['shareType'] {
	if (!shareType) {
		throw new Error('Share type is null or undefined')
	}
	return shareType.toLowerCase() === 'plant' ? 'store' : (shareType.toLowerCase() as ReportInput['shareType'])
}

export function apiParameterToDashboardReportParameter(
	param: NonNullable<DashboardReportMetadataQuery$result['dashboardReport']>['parameterValues'][number],
): NoQueryValues<DashboardReportParameter> {
	//
	if (!param?.name || !param.title || !param.type) {
		throw new Error('Parameter is null or undefined')
	}

	if (param.__typename === 'DashboardReportDateParameter') {
		return {
			name: param.name,
			type: 'date' as const,
			title: param.title,
			default: param.default,
			value: param.value ?? param.default,
		}
	} else if (param.__typename === 'DashboardReportDateRangeParameter') {
		const parameter: NoQueryValues<DashboardReportDateRangeParameter> = {
			name: param.name,
			type: 'dateRange' as const,
			title: param.title,
			default: param.default,
			value: (param.value ?? param.default) as NamedDateRange | 'Custom',
			dates: {
				from: param.dates?.from ?? '',
				to: param.dates?.to ?? '',
			},
		}
		return parameter
	} else if (param.__typename === 'DashboardReportSelectionParameter') {
		const parameter: NoQueryValues<DashboardReportSelectionParameter> = {
			name: param.name,
			type: 'selection' as const,
			title: param.title,
			default: param.default,
			value: param.value ?? param.default,
			optionList: fixOptionList(param.optionList ?? []),
		}
		return parameter
	} else if (param.__typename === 'DashboardReportSiteSelectionParameter') {
		const parameter: NoQueryValues<DashboardReportSiteSelectionParameter> = {
			name: param.name,
			type: 'siteSelection' as const,
			title: param.title,
			default: param.default,
			value: param.value ?? param.default,
			optionList: fixOptionList(param.optionList ?? []),
		}
		return parameter
	} else if (param.__typename === 'DashboardReportSessionParameter') {
		return {
			default: param.default,
			name: param.name,
			type: 'session' as const,
			title: param.title,
			value: param.value ?? param.default,
		}
	}

	//@ts-expect-error strictness check please ignore
	// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
	throw new Error(`Unknown parameter type: ${param.__typename ?? 'null'}`)
}

export function dashboardReportParameterToParameter(p: DashboardReportParameter): Parameter {
	const type = p.type
	if (type === 'selection') {
		return {
			...p,
			type,
			optionList: p.optionList ?? [],
		}
	} else if (type === 'dateRange') {
		return {
			...p,
			type,
			default: (p.default as NamedDateRange) ?? 'This Year',
			optionList: undefined,
		}
	} else {
		return {
			...p,
			type,
			optionList: undefined,
		}
	}
}

// #region GraphQL Documents
export const chartDataQuery = graphql(`
	query ChartDataQuery(
		$parameterSelectionList: [DashboardReportParametersInput]
		$dashboardReportId: Int!
		$chartArgs: DashboardReportChartsArgs
	) {
		dashboardReport(parameters: $parameterSelectionList, id: $dashboardReportId) {
			id
			charts(args: $chartArgs) {
				chartId: id
				name
				title
				rank
				type
				supertype
				formatting
				json: jsonCombined
				dataTable {
					... on GoogleDataTable {
						rows
						cols {
							id
							label
							type
							role
						}
					}
					... on RactiveDataTable {
						columns {
							class
							name
							property
						}
						rows
					}
				}
				multiSeries {
					value
					series
					tooltip
					groupXAxisBy
				}
			}
			parameterValues {
				... on DashboardReportDateParameter {
					name
					type
					title
					default
					value
				}
				... on DashboardReportDateRangeParameter {
					name
					type
					title
					default
					value
					dates {
						from
						to
					}
				}
				... on DashboardReportSelectionParameter {
					name
					type
					title
					default
					value
					optionList {
						id
						name
						fromQuery
						rawSql
					}
				}
				... on DashboardReportSessionParameter {
					name
					type
					title
					default
					value
				}
				... on DashboardReportSiteSelectionParameter {
					name
					type
					title
					default
					value
					permission
					optionList {
						id
						name
						fromQuery
					}
				}
			}
		}
	}
`)

export const dashboardReportQuery = graphql(`
	query DashboardReportMetadataQuery($dashboardReportId: Int!, $chartArgs: DashboardReportChartsArgs) {
		dashboardReport(id: $dashboardReportId) {
			dashboardReportId: id
			title
			ownerId
			autoRefreshInterval
			name
			shareId
			shareType
			ownerId
			parameterValues {
				... on DashboardReportDateRangeParameter {
					name
					type
					title
					default
					value
					dates {
						from
						to
					}
				}
				... on DashboardReportDateParameter {
					name
					type
					title
					default
					value
				}
				... on DashboardReportSelectionParameter {
					name
					type
					title
					default
					value
					optionList {
						id
						name
						rawSql
						fromQuery
					}
				}
				... on DashboardReportSessionParameter {
					name
					type
					title
					default
					value
				}
				... on DashboardReportSiteSelectionParameter {
					name
					type
					title
					default
					value
					permission
					optionList {
						id
						name
						fromQuery
					}
				}
			}
			charts(args: $chartArgs) {
				chartId: id
				name
				title
				rank
				json: jsonCombined
				supertype
				showChart: display
			}
		}
	}
`)

const reportTitlesQuery = graphql(`
	query ReportTitlesQuery($args: DashboardReportArgs) {
		dashboardReports(args: $args) {
			dashboardReportId: id
			name
			title
			ownerId
		}
	}
`)
// #endregion
